import React from 'react';
import PropTypes from 'prop-types';
import Chart from 'react-apexcharts';
import { readablePrice } from '../../../utils/getCurrency';
import {
  getIntervalAndMax,
  getNonZeroAxisSeries,
} from '../../../utils/nativeAnalyticsUtils';
import {
  BUSY_MODE_KEYS,
  ORDER_COUNT_KEYS,
} from '../../../config/constants/analytics';
import { getBaseChartOptions, mergeChartOptions } from '../theme/apexTheme';
import { formatMinutesToHours } from '../../../utils/format-date';

const BarChart = ({
  id,
  xAxisData,
  yAxisData,
  seriesName,
  currencyObj,
  exportedCsvHeaderTitle,
}) => {
  const isOrderCountChart = Object.values(ORDER_COUNT_KEYS).includes(id);
  const isBusyModeChart = Object.values(BUSY_MODE_KEYS).includes(id);
  const maxValueOfSeries = Math.max(...yAxisData);
  const { yAxisMaxValue, yAxisTickAmount } =
    getIntervalAndMax(maxValueOfSeries) || {};

  const { xAxisSeries, yAxisSeries } = getNonZeroAxisSeries(
    xAxisData,
    yAxisData,
  );

  const formatBarChartLabel = value => {
    if (isBusyModeChart) {
      return formatMinutesToHours(value);
    }
    const floatPointCount = isOrderCountChart ? 0 : 2;
    return value.toFixed(floatPointCount);
  };

  const formatBarChartTooltip = value => {
    if (isOrderCountChart) {
      return value;
    }
    if (isBusyModeChart) {
      return formatMinutesToHours(value);
    }
    return readablePrice(value * 100, currencyObj);
  };

  const chartConfig = {
    options: mergeChartOptions(getBaseChartOptions(), {
      colors: ['#6A13F4'],
      stroke: { width: 0 },
      tooltip: {
        y: {
          formatter(value) {
            return formatBarChartTooltip(value);
          },
        },
      },
      chart: {
        id,
        type: 'bar',
        toolbar: {
          show: true,
          offsetX: 0,
          offsetY: 0,
          export: {
            csv: {
              filename: id,
              headerCategory: exportedCsvHeaderTitle,
              headerValue: 'value',
            },
            svg: { filename: id },
            png: { filename: id },
          },
        },
      },
      xaxis: {
        type: 'category',
        categories: xAxisSeries,
        labels: { hideOverlappingLabels: false, trim: true },
      },
      yaxis: {
        max: yAxisMaxValue || 6,
        tickAmount: yAxisTickAmount || 1,
        labels: {
          formatter(value) {
            return formatBarChartLabel(value);
          },
        },
      },
    }),
    series: [
      {
        name: seriesName,
        data: yAxisSeries,
      },
    ],
  };

  return (
    <Chart
      options={chartConfig.options}
      series={chartConfig.series}
      type="bar"
      width="100%"
      height="350"
    />
  );
};

BarChart.propTypes = {
  id: PropTypes.string,
  xAxisData: PropTypes.array,
  yAxisData: PropTypes.array,
  seriesName: PropTypes.string,
  currencyObj: PropTypes.object,
  exportedCsvHeaderTitle: PropTypes.string,
};

export default BarChart;
